import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Loader2, Trash2 } from "lucide-react";
import type { OrgMembership } from "@/contexts/ActiveOrgContext";
import { OrgCard } from "./OrgCard";

interface OrgGridProps {
  memberships: OrgMembership[];
  onEnter: (id: string) => void;
}

export function OrgGrid({ memberships, onEnter }: OrgGridProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<OrgMembership | null>(null);
  const [confirmName, setConfirmName] = useState("");

  const orgIds = useMemo(() => memberships.map(m => m.organization_id), [memberships]);
  const orgIdsKey = useMemo(() => [...orgIds].sort().join(","), [orgIds]);

  const { data: dossierCounts = {} } = useQuery({
    queryKey: ["hub-dossier-counts", orgIdsKey],
    queryFn: async () => {
      if (orgIds.length === 0) return {};
      const { data, error } = await supabase
        .from("dossiers")
        .select("organization_id")
        .in("organization_id", orgIds);
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach(d => {
        counts[d.organization_id] = (counts[d.organization_id] || 0) + 1;
      });
      return counts;
    },
    enabled: orgIds.length > 0,
  });

  const { data: lastActivities = {} } = useQuery({
    queryKey: ["hub-last-activities", orgIdsKey],
    queryFn: async () => {
      if (orgIds.length === 0) return {};
      const { data, error } = await supabase
        .from("dossiers")
        .select("organization_id, updated_at")
        .in("organization_id", orgIds)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      const activities: Record<string, string> = {};
      (data ?? []).forEach(d => {
        if (!activities[d.organization_id]) {
          activities[d.organization_id] = d.updated_at;
        }
      });
      return activities;
    },
    enabled: orgIds.length > 0,
  });

  const deleteMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const { error } = await supabase.from("organizations").delete().eq("id", orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Organisation supprimée");
      queryClient.invalidateQueries({ queryKey: ["org-memberships"] });
      queryClient.invalidateQueries({ queryKey: ["hub-dossier-counts"] });
      setDeleteTarget(null);
      setConfirmName("");
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {memberships.map(m => (
          <OrgCard
            key={m.id}
            membership={m}
            onEnter={() => onEnter(m.organization_id)}
            onDelete={() => setDeleteTarget(m)}
            dossierCount={dossierCounts[m.organization_id] ?? 0}
            lastActivity={lastActivities[m.organization_id] ?? null}
          />
        ))}
        <Card
          className="border-dashed hover:border-primary/40 hover:bg-primary/5 transition-all cursor-pointer flex items-center justify-center min-h-[180px]"
          onClick={() => navigate("/onboarding?new=true")}
        >
          <CardContent className="flex flex-col items-center gap-3 py-8">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Plus className="w-6 h-6 text-primary" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">Créer une organisation</p>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setConfirmName(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {deleteTarget?.organization?.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est <strong>irréversible</strong>. Tous les dossiers, données et membres seront supprimés.
              <br /><br />
              Tapez <strong>{deleteTarget?.organization?.name}</strong> pour confirmer :
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={deleteTarget?.organization?.name} className="mt-2" />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteTarget(null); setConfirmName(""); }}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmName !== deleteTarget?.organization?.name || deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.organization_id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
